import { useContext, type ReactNode } from "react";

import {
  Button as ReactAriaButton,
  Disclosure as ReactAriaDisclosure,
  type DisclosureProps as ReactAriaDisclosureProps,
  DisclosurePanel as ReactAriaDisclosurePanel,
  DisclosureStateContext as ReactAriaDisclosureStateContext,
  Heading as ReactAriaHeading,
} from "react-aria-components";

import { useUIVersion } from "#hooks";
import { CaretRightIcon } from "#icons";
import { customTv } from "#utils";

const headerStyles = customTv({
  base: "flex w-full items-center outline-none text-title-medium gap-x-md text-text-primary",
  variants: {
    isDisabled: {
      true: "cursor-not-allowed opacity-50",
      false: "cursor-pointer",
    },
  },
  defaultVariants: {
    isDisabled: false,
  },
});

const panelStyles = customTv({
  base: "grid overflow-hidden transition-all duration-300 ease-in-out",
  variants: {
    isExpanded: {
      true: "grid-rows-[1fr]",
      false: "grid-rows-[0fr]",
    },
  },
  defaultVariants: {
    isExpanded: false,
  },
});

const chevronStyles = customTv({
  base: "shrink-0 transition-transform duration-200 text-text-primary",
  variants: {
    isExpanded: {
      true: "",
      false: "",
    },
    uiVersion: {
      v1: "",
      v2: "",
    },
  },
  compoundVariants: [
    { uiVersion: "v1", isExpanded: false, className: "rotate-0" },
    { uiVersion: "v1", isExpanded: true, className: "rotate-90" },
    { uiVersion: "v2", isExpanded: false, className: "rotate-90" },
    { uiVersion: "v2", isExpanded: true, className: "rotate-[270deg]" },
  ],
  defaultVariants: {
    uiVersion: "v2",
  },
});

export interface DisclosureProps extends ReactAriaDisclosureProps {
  children: ReactNode;
}

export function Disclosure({ children, ...props }: DisclosureProps) {
  return <ReactAriaDisclosure {...props}>{children}</ReactAriaDisclosure>;
}

export interface DisclosureHeaderProps {
  children: ReactNode;
  className?: string;
  iconClassName?: string;
  collapsed?: boolean;
  position?: "leading" | "trailing";
}

export function DisclosureHeader({
  children,
  className,
  iconClassName,
  collapsed = false,
  position = "trailing",
}: DisclosureHeaderProps) {
  const { isExpanded } = useContext(ReactAriaDisclosureStateContext) ?? {
    isExpanded: false,
  };
  const uiVersion = useUIVersion();
  const resolvedPosition = uiVersion === "v1" ? "leading" : position;

  const chevron = !collapsed ? (
    <CaretRightIcon
      className={chevronStyles({
        isExpanded,
        uiVersion,
        class: iconClassName,
      })}
      size={16}
      aria-hidden
    />
  ) : null;

  return (
    <ReactAriaHeading>
      <ReactAriaButton
        className={({ isDisabled }) =>
          headerStyles({
            isDisabled,
            className,
          })
        }
        slot="trigger"
      >
        {resolvedPosition === "leading" ? chevron : null}
        <span className="flex-1 text-start">{children}</span>
        {resolvedPosition === "trailing" ? chevron : null}
      </ReactAriaButton>
    </ReactAriaHeading>
  );
}

export interface DisclosurePanelProps {
  children: ReactNode;
  className?: string;
}

export function DisclosurePanel({ children, className }: DisclosurePanelProps) {
  const { isExpanded } = useContext(ReactAriaDisclosureStateContext) ?? {
    isExpanded: false,
  };

  return (
    <ReactAriaDisclosurePanel
      className={panelStyles({
        isExpanded,
        className,
      })}
      // To allow animation during hiding the panel
      style={{ contentVisibility: "auto" }}
    >
      <div className="overflow-hidden">{children}</div>
    </ReactAriaDisclosurePanel>
  );
}
